import type { Assessment, AssessmentRuleResult } from "@/services/api/case-checking";
import type { RuleSetVersion } from "@/services/api/case-checking/rule-sets";

/** Normalise case type for rule-scope checks (matches Assessment tab). */
export function activeCaseTypeForScope(caseType: string | null | undefined): string | null {
  return caseType && caseType !== "unknown" ? caseType : null;
}

/** Whether a rule applies to the active case type (universal rules always apply). */
export function ruleAppliesToCase(
  activeCaseType: string | null,
  applicableCaseTypes: string[] | undefined,
): boolean {
  if (!applicableCaseTypes || applicableCaseTypes.length === 0) return true;
  if (!activeCaseType) return false;
  return applicableCaseTypes.includes(activeCaseType);
}

function buildRuleApplicabilityMap(
  ruleSet: RuleSetVersion | null | undefined,
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  if (!ruleSet) return map;
  for (const rule of ruleSet.rules) {
    map.set(rule.id, rule.applicable_case_types ?? []);
  }
  return map;
}

/** Assessment rule results visible for this case (same filter as the Rules tab). */
export function getInScopeAssessmentRules(
  assessment: Assessment | null | undefined,
  ruleSet: RuleSetVersion | null | undefined,
  caseType: string | null | undefined,
): AssessmentRuleResult[] {
  if (!assessment?.groups?.length) return [];

  const activeCaseType = activeCaseTypeForScope(caseType);
  const applicability = buildRuleApplicabilityMap(ruleSet);
  const canFilter = applicability.size > 0;

  return assessment.groups.flatMap((g) => g.rules).filter((rule) => {
    if (!canFilter) return true;
    return ruleAppliesToCase(activeCaseType, applicability.get(rule.rule_id));
  });
}

export function countRuleOutcomes(rules: AssessmentRuleResult[]): {
  failed: number;
  unclear: number;
} {
  return {
    failed: rules.filter((r) => r.status === "failed").length,
    unclear: rules.filter((r) => r.status === "unclear").length,
  };
}

/** Overview nav copy — e.g. "2 failed · 4 unclear". */
export function formatRuleAttentionLabel(failed: number, unclear: number): string | undefined {
  if (failed === 0 && unclear === 0) return undefined;
  const parts: string[] = [];
  if (failed > 0) parts.push(`${failed} failed`);
  if (unclear > 0) parts.push(`${unclear} unclear`);
  return parts.join(" · ");
}

export interface RulesTabBadge {
  count?: number;
  countLabel?: string;
  countVariant: "neutral" | "attention" | "warning";
}

/** Rules tab pill — red failures take priority; unclear-only shows amber. */
export function rulesTabBadge(failed: number, unclear: number): RulesTabBadge {
  if (failed > 0) {
    return {
      count: failed,
      countLabel: `${failed} failed`,
      countVariant: "attention",
    };
  }
  if (unclear > 0) {
    return {
      count: unclear,
      countLabel: `${unclear} unclear`,
      countVariant: "warning",
    };
  }
  return { countVariant: "neutral" };
}
